// Utility functions for clipboard operations with fallbacks
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    // First try the modern Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      console.log('Text copied using Clipboard API');
      return true;
    }

    // Fallback to the older document.execCommand method
    return fallbackCopyToClipboard(text);
  } catch (error) {
    console.warn('Clipboard API failed, trying fallback:', error);
    return fallbackCopyToClipboard(text);
  }
};

// Fallback method using document.execCommand (deprecated but still works)
const fallbackCopyToClipboard = (text: string): boolean => {
  try {
    // Create a temporary textarea element
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-999999px';
    textarea.style.top = '-999999px';
    textarea.setAttribute('readonly', '');

    document.body.appendChild(textarea);

    // Select and copy the text
    textarea.select();
    textarea.setSelectionRange(0, 99999); // For mobile devices

    const successful = document.execCommand('copy');
    document.body.removeChild(textarea);

    if (successful) {
      console.log('Text copied using execCommand fallback');
    } else {
      console.warn('execCommand copy failed');
    }

    return successful;
  } catch (error) {
    console.error('Fallback copy failed:', error);
    return false;
  }
};

// Read from clipboard with fallback
export const readFromClipboard = async (): Promise<string | null> => {
  try {
    // Try the modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      return await navigator.clipboard.readText();
    }

    // No reliable fallback for reading clipboard
    console.warn('Clipboard read not supported in this environment');
    return null;
  } catch (error) {
    console.warn('Failed to read from clipboard:', error);
    return null;
  }
};

// Check if clipboard operations are supported
export const isClipboardSupported = (): boolean => {
  return !!(navigator.clipboard || document.execCommand);
};

/**
 * Safe wrapper for async clipboard operations with user feedback
 * Shows user feedback for copy operations
 */
export const copyWithFeedback = async (
  text: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
): Promise<void> => {
  try {
    const success = await copyToClipboard(text);
    if (success) {
      onSuccess?.();
    } else {
      onError?.('Failed to copy to clipboard');
    }
  } catch (error) {
    console.error('Copy operation error:', error);
    onError?.(
      error instanceof Error ? error.message : 'Failed to copy to clipboard'
    );
  }
};
